"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STANDARD_NOTES = exports.normalizeNoteName = exports.getNoteNumbers = exports.getFormattedNoteNames = exports.getNoteNames = exports.sortNotes = exports.deserializeNote = exports.parseNote = exports.getNotes = exports.getNote = exports.STANDARD_NOTE_NAMES = void 0;
const format_1 = require("./util/format");
// Integer notation                                      0    1     2    3     4    5    6     7    8     9    10    11
exports.STANDARD_NOTE_NAMES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
/**
 * Pool of immutable note instances where the key is note name and octave.
 * At most there will be 21 * number of octaves.
 */
const notePool = {};
const NOTES_BY_NAME = {
    "B#": { name: "B#", alias: "C", number: 0 },
    "C": { name: "C", alias: "B#", number: 0 },
    "C#": { name: "C#", alias: "Db", number: 1 },
    "Db": { name: "Db", alias: "C#", number: 1 },
    "D": { name: "D", alias: "", number: 2 },
    "D#": { name: "D#", alias: "Eb", number: 3 },
    "Eb": { name: "Eb", alias: "D#", number: 3 },
    "E": { name: "E", alias: "Fb", number: 4 },
    "Fb": { name: "Fb", alias: "E", number: 4 },
    "E#": { name: "E#", alias: "F", number: 5 },
    "F": { name: "F", alias: "E#", number: 5 },
    "F#": { name: "F#", alias: "Gb", number: 6 },
    "Gb": { name: "Gb", alias: "F#", number: 6 },
    "G": { name: "G", alias: "", number: 7 },
    "G#": { name: "G#", alias: "Ab", number: 8 },
    "Ab": { name: "Ab", alias: "G#", number: 8 },
    "A": { name: "A", alias: "", number: 9 },
    "A#": { name: "A#", alias: "Bb", number: 10 },
    "Bb": { name: "Bb", alias: "A#", number: 10 },
    "B": { name: "B", alias: "Cb", number: 11 },
    "Cb": { name: "Cb", alias: "B", number: 11 },
};
/**
 * The implementation of a Note
 */
class NoteImpl {
    constructor(name, octave = 4) {
        const noteInfo = NOTES_BY_NAME[name];
        if (!noteInfo) {
            throw new Error(name + " is not a valid note name");
        }
        this.octave = octave;
        this.name = noteInfo.name;
        this.number = noteInfo.number;
        this.aliasName = noteInfo.alias;
    }
    get midiNumber() {
        return this.octave * 12 + this.number + 12;
    }
    get keyNumber() {
        return this.midiNumber - 20;
    }
    get alias() {
        return this.aliasName ? getNote(this.aliasName, this.octave) : undefined;
    }
    get formattedName() {
        return (0, format_1.formatAccidentals)(this.name);
    }
    get accidental() {
        return this.name.charAt(1);
    }
    get hasAccidental() {
        return this.accidental.length > 0;
    }
    get isSharp() {
        return this.accidental === "#";
    }
    get isFlat() {
        return this.accidental === "b";
    }
    transpose(steps) {
        return getNote(this.number + steps, this.octave);
    }
    equals(note) {
        return this.number === note.number && this.octave === note.octave;
    }
    equalsIgnoreOctave(note) {
        return this.number === note.number;
    }
    isSameAs(note) {
        return this.equalsIgnoreOctave(note);
    }
    toString(formatted = false) {
        return (formatted ? (0, format_1.formatAccidentals)(this.name) : this.name) + this.octave;
    }
    toJSON() {
        return this.toString();
    }
}
function getNote(nameOrNumber, octave = 4) {
    let noteName;
    if (typeof nameOrNumber === "string") {
        noteName = nameOrNumber;
    }
    else if (typeof nameOrNumber === "number") {
        const num = Math.abs((nameOrNumber < 0 ? 12 + (nameOrNumber % 12) : nameOrNumber) % 12);
        octave = octave + Math.floor(nameOrNumber / 12);
        noteName = exports.STANDARD_NOTE_NAMES[num];
    }
    else {
        throw new Error("First parameter must be a note name or number");
    }
    const key = noteName + octave;
    let note = notePool[key];
    if (!note) {
        notePool[key] = note = new NoteImpl(noteName, octave);
    }
    return note;
}
exports.getNote = getNote;
function getNotes(...nameOrNumber) {
    return nameOrNumber.map(n => {
        if (typeof n === "string") {
            return parseNote(n);
        }
        else if (typeof n === "number") {
            return getNote(n);
        }
        else {
            throw new Error("Value must be a string or number");
        }
    });
}
exports.getNotes = getNotes;
/**
 * Parses a note name into a Note object, or undefined if invalid
 * @param note Note name with optional number for octave, e.g. C#6
 */
function parseNote(note) {
    const re = /([A-G][#,b]?)(\d?)/.exec(note);
    if (re && re[1]) {
        const octave = re[2] ? parseInt(re[2], 10) : undefined;
        return getNote(re[1], octave);
    }
    return undefined;
}
exports.parseNote = parseNote;
/**
 * Deserializes a note that was serialized to JSON
 * @param value JSON value
 */
function deserializeNote(value) {
    return parseNote(value);
}
exports.deserializeNote = deserializeNote;
/**
* Sorts a set of notes into natural order on the musical scale starting with the root note
* @param notes An array of notes
* @param rootNote (optional) The root note, if not defined uses the first note in the notes array
*/
function sortNotes(notes, root = notes[0]) {
    return notes.sort((a, b) => {
        const an = (a.number < root.number ? a.number + 12 : a.number);
        const bn = (b.number < root.number ? b.number + 12 : b.number);
        return an - bn;
    });
}
exports.sortNotes = sortNotes;
/**
 * Gets an array of names from an array of notes
 */
function getNoteNames(notes) {
    return notes.map(n => n.name);
}
exports.getNoteNames = getNoteNames;
/**
 * Gets an array of formatted names (using formatAccidentals) from an array of notes
 */
function getFormattedNoteNames(notes) {
    return notes.map(n => n.formattedName);
}
exports.getFormattedNoteNames = getFormattedNoteNames;
/**
 * Gets an array of note numbers from an array of notes
 */
function getNoteNumbers(notes) {
    return notes.map(n => n.number);
}
exports.getNoteNumbers = getNoteNumbers;
/** Normalizes a note name to one of the standard names */
function normalizeNoteName(noteName) {
    return noteName === "Db" ? "C#" :
        noteName === "D#" ? "Eb" :
            noteName === "E#" ? "F" :
                noteName === "Fb" ? "E" :
                    noteName === "Gb" ? "F#" :
                        noteName === "G#" ? "Ab" :
                            noteName === "A#" ? "Bb" :
                                noteName === "B#" ? "C" :
                                    noteName === "Cb" ? "B" :
                                        noteName;
}
exports.normalizeNoteName = normalizeNoteName;
/** Standard notes in natural order starting with C */
exports.STANDARD_NOTES = exports.STANDARD_NOTE_NAMES.map(n => getNote(n));
//# sourceMappingURL=notes.js.map